import { useState, useEffect } from 'react';
import { fetchSettings, updateSettings, exportObsidian, exportFull, type IntegrationStatus } from '../lib/api.js';

interface IntegrationCardProps {
  integration: IntegrationStatus;
  onSaved: () => void;
}

function IntegrationCard({ integration, onSaved }: IntegrationCardProps) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  async function handleSave() {
    const trimmed = value.trim();
    if (!trimmed) return;
    setSaving(true);
    setError(null);
    try {
      await updateSettings({ [integration.key]: trimmed });
      setSaved(true);
      setValue('');
      setEditing(false);
      setShow(false);
      setTimeout(() => setSaved(false), 3000);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="border border-obsidian-border rounded-lg overflow-hidden">
      {/* Header row */}
      <div className="flex items-start justify-between gap-4 p-4">
        <div className="flex items-start gap-3 min-w-0">
          <span className="text-xl flex-shrink-0 mt-0.5">{integration.icon}</span>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-obsidian-text">{integration.label}</span>
              {saved ? (
                <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid #10b981', color: '#10b981' }}>
                  ✓ Saved
                </span>
              ) : integration.configured ? (
                <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', color: '#6ee7b7' }}>
                  ✓ Configured
                </span>
              ) : (
                <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: '#6b7280' }}>
                  ○ Not configured
                </span>
              )}
            </div>
            <p className="text-xs text-obsidian-text-muted mt-1 leading-relaxed">{integration.description}</p>
            <a
              href={integration.docUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-obsidian-accent hover:underline mt-0.5 inline-block"
            >
              Get API key →
            </a>
          </div>
        </div>
        <button
          onClick={() => { setEditing((e) => !e); setError(null); setValue(''); }}
          className="text-xs px-3 py-1.5 rounded-lg flex-shrink-0 transition-all"
          style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#9ca3af' }}
        >
          {editing ? 'Cancel' : integration.configured ? 'Update' : 'Set key'}
        </button>
      </div>

      {/* Inline edit form */}
      {editing && (
        <div className="px-4 pb-4 border-t border-obsidian-border pt-3" style={{ background: 'rgba(255,255,255,0.02)' }}>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <input
                type={show ? 'text' : 'password'}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                placeholder={`Paste your ${integration.label}...`}
                autoFocus
                className="w-full bg-obsidian-bg-secondary border border-obsidian-border rounded-lg px-3 py-2 text-sm text-obsidian-text placeholder:text-obsidian-text-faint focus:outline-none focus:border-obsidian-accent transition-colors pr-10"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-obsidian-text-faint hover:text-obsidian-text transition-colors text-xs"
                tabIndex={-1}
              >
                {show ? '🙈' : '👁'}
              </button>
            </div>
            <button
              onClick={handleSave}
              disabled={!value.trim() || saving}
              className="px-4 py-2 text-sm font-semibold rounded-lg transition-all"
              style={{ background: 'rgba(99,102,241,0.2)', border: '1px solid #6366f1', color: '#818cf8', opacity: (!value.trim() || saving) ? 0.4 : 1 }}
            >
              {saving ? '...' : 'Save'}
            </button>
          </div>
          {error && <p className="text-xs text-red-400 mt-1.5">{error}</p>}
          <p className="text-xs text-obsidian-text-faint mt-1.5">
            Stored securely in AWS Secrets Manager. Changes take effect within a few minutes.
          </p>
        </div>
      )}
    </div>
  );
}

export default function SettingsView() {
  const [integrations, setIntegrations] = useState<IntegrationStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [exportingFull, setExportingFull] = useState(false);
  const [exportFullUrl, setExportFullUrl] = useState<string | null>(null);
  const [exportingObsidian, setExportingObsidian] = useState(false);
  const [exportObsidianUrl, setExportObsidianUrl] = useState<string | null>(null);

  async function handleExportFull() {
    setExportingFull(true); setExportFullUrl(null);
    try {
      const { url } = await exportFull();
      setExportFullUrl(url);
    } catch {
      alert('Export failed. Try again.');
    } finally { setExportingFull(false); }
  }

  async function handleExportObsidian() {
    setExportingObsidian(true); setExportObsidianUrl(null);
    try {
      const { url } = await exportObsidian();
      setExportObsidianUrl(url);
    } catch {
      alert('Export failed. Try again.');
    } finally { setExportingObsidian(false); }
  }

  function load() {
    setLoading(true);
    fetchSettings()
      .then((data) => setIntegrations(data.integrations))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load settings'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-2xl mx-auto px-6 py-6">
      <div className="mb-6">
        <h1 className="text-lg font-semibold text-obsidian-text mb-1">Settings</h1>
        <p className="text-sm text-obsidian-text-muted">Configure optional integrations to unlock additional features.</p>
      </div>

      {loading && (
        <div className="text-center py-12">
          <p className="text-sm text-obsidian-text-muted">Loading...</p>
        </div>
      )}

      {error && !loading && (
        <p className="text-sm text-red-400">{error}</p>
      )}

      {!loading && !error && (
        <div>
          <h2 className="text-xs font-semibold text-obsidian-text-faint uppercase tracking-wide mb-3">
            API Integrations
          </h2>
          <div className="space-y-3">
            {integrations.map((integration) => (
              <IntegrationCard
                key={integration.key}
                integration={integration}
                onSaved={load}
              />
            ))}
          </div>
        </div>
      )}

      {/* Export / Import */}
      <div className="mt-8 border border-obsidian-border rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-obsidian-border bg-obsidian-surface">
          <h2 className="text-sm font-semibold text-obsidian-text">Export &amp; Import</h2>
          <p className="text-xs text-obsidian-text-muted mt-0.5">Back up or migrate your entire knowledge base</p>
        </div>
        <div className="p-4 space-y-4">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-48">
              <div className="text-sm font-medium text-obsidian-text mb-1">Full Data Export</div>
              <p className="text-xs text-obsidian-text-muted mb-3">All insights (all statuses), sources, Telegram activity, and categories as a ZIP archive.</p>
              <button
                onClick={() => void handleExportFull()}
                disabled={exportingFull}
                className="btn-primary text-sm px-4 py-2 disabled:opacity-50"
              >
                {exportingFull ? 'Preparing...' : 'Export All Data →'}
              </button>
              {exportFullUrl && (
                <div className="mt-2">
                  <a href={exportFullUrl} download className="text-xs text-obsidian-accent underline">
                    ↓ Download export ZIP (expires in 24h)
                  </a>
                </div>
              )}
            </div>
            <div className="flex-1 min-w-48">
              <div className="text-sm font-medium text-obsidian-text mb-1">Obsidian Export</div>
              <p className="text-xs text-obsidian-text-muted mb-3">Published insights as Markdown files with wikilinks, ready for your Obsidian vault.</p>
              <button
                onClick={() => void handleExportObsidian()}
                disabled={exportingObsidian}
                className="btn-primary text-sm px-4 py-2 disabled:opacity-50"
              >
                {exportingObsidian ? 'Preparing...' : 'Export for Obsidian →'}
              </button>
              {exportObsidianUrl && (
                <div className="mt-2">
                  <a href={exportObsidianUrl} download className="text-xs text-obsidian-accent underline">
                    ↓ Download Obsidian ZIP (expires in 15 min)
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
